import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import artistApi from '@/lib/artistApi';
import SEO from '@/components/SEO';
import { useArtistAuth } from '@/context/ArtistAuthContext';
import { buildFramesFromPreset, defaultFrameStyles, exhibitionWallPresets, wallPalettes } from '@/lib/exhibitionWallLayouts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const WALL_WIDTH = 920;
const WALL_HEIGHT = 580;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const withProxy = (url) => `${BACKEND_URL}/api/artwork-image-proxy?url=${encodeURIComponent(url)}`;

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });

export default function ExhibitionWall() {
  const { artist } = useArtistAuth();
  const wallRef = useRef(null);
  const dragStateRef = useRef(null);
  const [artworks, setArtworks] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [frames, setFrames] = useState([]);
  const [selectedFrameId, setSelectedFrameId] = useState('');
  const [preset, setPreset] = useState('solo-statement');
  const [frameStyle, setFrameStyle] = useState(defaultFrameStyles[0].id);
  const [wallColor, setWallColor] = useState(wallPalettes[0].value);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);

  const selectedArtworks = useMemo(
    () => artworks.filter((artwork) => selectedIds.includes(artwork.id)),
    [artworks, selectedIds]
  );

  const selectedFrame = useMemo(
    () => frames.find((item) => item.id === selectedFrameId) || null,
    [frames, selectedFrameId]
  );

  useEffect(() => {
    let mounted = true;
    const fetchArtworks = async () => {
      if (!artist?.id) return;
      setLoading(true);
      try {
        const response = await artistApi.get(`/artists/${artist.id}/approved-submissions?limit=24`);
        if (mounted) {
          const nextArtworks = response.data?.submissions || [];
          setArtworks(nextArtworks);
          setSelectedIds(nextArtworks.slice(0, Math.min(4, nextArtworks.length)).map((item) => item.id));
        }
      } catch (fetchError) {
        if (mounted) {
          setError(fetchError.response?.data?.detail || 'Unable to load your approved artworks right now.');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchArtworks();
    return () => {
      mounted = false;
    };
  }, [artist?.id]);

  useEffect(() => {
    if (!selectedArtworks.length) {
      setFrames([]);
      setSelectedFrameId('');
      return;
    }
    const nextFrames = buildFramesFromPreset(preset, selectedArtworks);
    setFrames(nextFrames);
    setSelectedFrameId((current) => current || nextFrames[0]?.id || '');
  }, [preset, selectedArtworks]);

  const frameStyleConfig = defaultFrameStyles.find((item) => item.id === frameStyle) || defaultFrameStyles[0];

  const toggleArtwork = (artworkId) => {
    setMessage('');
    setError('');
    setSelectedIds((current) => {
      if (current.includes(artworkId)) {
        const next = current.filter((id) => id !== artworkId);
        return next;
      }
      if (current.length >= 8) {
        setError('You can place up to 8 artworks on one exhibition wall.');
        return current;
      }
      return [...current, artworkId];
    });
  };

  const updateSelectedFrame = (updates) => {
    if (!selectedFrameId) return;
    setFrames((current) =>
      current.map((frame) => (frame.id === selectedFrameId ? { ...frame, ...updates } : frame))
    );
  };

  const handlePointerDown = (event, frame) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedFrameId(frame.id);
    const wallRect = wallRef.current?.getBoundingClientRect();
    if (!wallRect) return;
    dragStateRef.current = {
      id: frame.id,
      startX: event.clientX,
      startY: event.clientY,
      originX: frame.x,
      originY: frame.y,
      wallRect,
    };
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  const handlePointerMove = useCallback((event) => {
    const state = dragStateRef.current;
    if (!state) return;
    const deltaX = ((event.clientX - state.startX) / state.wallRect.width) * 100;
    const deltaY = ((event.clientY - state.startY) / state.wallRect.height) * 100;
    setFrames((current) =>
      current.map((frame) =>
        frame.id === state.id
          ? {
              ...frame,
              x: clamp(state.originX + deltaX, 0, 100 - frame.w),
              y: clamp(state.originY + deltaY, 0, 100 - frame.h),
            }
          : frame
      )
    );
  }, []);

  const handlePointerUp = useCallback(() => {
    dragStateRef.current = null;
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', handlePointerUp);
  }, [handlePointerMove]);

  useEffect(() => () => handlePointerUp(), [handlePointerUp]);

  const exportWall = async () => {
    if (!selectedArtworks.length) {
      setError('Choose at least one approved artwork before exporting.');
      return;
    }
    setExporting(true);
    setError('');
    setMessage('');
    try {
      const canvas = document.createElement('canvas');
      canvas.width = WALL_WIDTH;
      canvas.height = WALL_HEIGHT;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = wallColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgba(15,23,42,0.08)';
      ctx.fillRect(0, canvas.height - 58, canvas.width, 58);

      for (const frame of frames) {
        const artwork = selectedArtworks.find((item) => item.id === frame.id);
        if (!artwork) continue;
        const x = (frame.x / 100) * canvas.width;
        const y = (frame.y / 100) * canvas.height;
        const w = (frame.w / 100) * canvas.width;
        const h = (frame.h / 100) * canvas.height;
        const border = Math.max(8, Math.round(Math.min(w, h) * 0.045));

        ctx.fillStyle = frameStyleConfig.shadow;
        ctx.fillRect(x + 10, y + 14, w, h);
        ctx.fillStyle = frameStyleConfig.border;
        ctx.fillRect(x, y, w, h);
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(x + border, y + border, w - border * 2, h - border * 2);

        const image = await loadImage(withProxy(artwork.artwork_image_url));
        const innerX = x + border + 4;
        const innerY = y + border + 4;
        const innerW = w - (border + 4) * 2;
        const innerH = h - (border + 4) * 2;

        const scale = Math.max(innerW / image.width, innerH / image.height);
        const drawW = image.width * scale;
        const drawH = image.height * scale;
        const drawX = innerX + (innerW - drawW) / 2;
        const drawY = innerY + (innerH - drawH) / 2;

        ctx.save();
        ctx.beginPath();
        ctx.rect(innerX, innerY, innerW, innerH);
        ctx.clip();
        ctx.drawImage(image, drawX, drawY, drawW, drawH);
        ctx.restore();
      }

      ctx.fillStyle = 'rgba(15, 23, 42, 0.75)';
      ctx.font = '600 22px Arial';
      ctx.fillText(`${artist?.name || 'KalaManch Artist'} Exhibition Wall`, 28, canvas.height - 20);

      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `${(artist?.name || 'kalamanch-artist').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-exhibition-wall.png`;
      link.click();
      setMessage('Exhibition wall PNG downloaded.');
    } catch (exportError) {
      setError('Export could not finish. One of the artwork image sources may be blocking image download for PNG generation.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#f7f9fc] pt-28">
      <SEO
        title="Exhibition Wall Simulator | KalaManch.art"
        description="Build a private digital exhibition wall using your approved KalaManch artworks, then export it as a presentation-ready PNG."
        keywords="KalaManch exhibition wall, artist tools, digital wall simulator, artwork wall mockup, artist presentation tool"
      />

      <section className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8">
        <div className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-xl sm:p-8">
          <div className="grid gap-8 xl:grid-cols-[300px_minmax(0,1fr)_280px]">
            <aside className="space-y-4">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.24em] text-[#FF6F00]">Artist Tool</p>
                <h1 className="mt-3 text-4xl font-black leading-tight text-gray-950">Exhibition Wall Simulator</h1>
                <p className="mt-4 text-sm leading-7 text-gray-600">
                  Pick your approved KalaManch artworks, place them on a virtual wall, and export a clean PNG for curators, collectors, or social sharing.
                </p>
              </div>

              <div className="rounded-[1.5rem] border border-orange-100 bg-orange-50 p-4 text-sm text-[#8A3B00]">
                Best with 3 to 8 artworks. You can still start with one and build the wall gradually.
              </div>

              <div className="rounded-[1.5rem] border border-slate-200 bg-[#f8fafc] p-4">
                <p className="text-xs font-black uppercase tracking-[0.22em] text-[#4A148C]">Your approved works</p>
                <div className="mt-3 space-y-3">
                  {loading ? (
                    <p className="text-sm text-gray-500">Loading approved artworks…</p>
                  ) : artworks.length === 0 ? (
                    <p className="text-sm text-gray-500">No approved competition artworks yet. Once admin approves your competition submissions, they will appear here.</p>
                  ) : (
                    artworks.map((artwork) => {
                      const active = selectedIds.includes(artwork.id);
                      return (
                        <button
                          key={artwork.id}
                          type="button"
                          onClick={() => toggleArtwork(artwork.id)}
                          className={`flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition ${
                            active
                              ? 'border-[#FF6F00] bg-orange-50 shadow-sm'
                              : 'border-slate-200 bg-white hover:border-orange-200'
                          }`}
                        >
                          <img
                            src={artwork.artwork_image_url}
                            alt={artwork.artwork_title || 'Artwork'}
                            className="h-16 w-16 rounded-xl object-cover"
                            loading="lazy"
                          />
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-black text-gray-950">{artwork.artwork_title || 'Untitled Artwork'}</span>
                            <span className="mt-1 block truncate text-xs font-semibold text-gray-500">
                              {[artwork.medium, artwork.category].filter(Boolean).join(' | ') || 'KalaManch approved artwork'}
                            </span>
                          </span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </aside>

            <section>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.22em] text-[#00A68C]">Preview wall</p>
                  <h2 className="mt-1 text-2xl font-black text-gray-950">Drag artworks to arrange your exhibition</h2>
                </div>
                <button type="button" onClick={exportWall} disabled={exporting} className="btn-primary">
                  {exporting ? 'Exporting…' : 'Download PNG'}
                </button>
              </div>

              <div
                ref={wallRef}
                className="relative overflow-hidden rounded-[2rem] border border-slate-200 shadow-inner"
                style={{
                  background: `linear-gradient(180deg, ${wallColor} 0%, ${wallColor} 82%, rgba(15,23,42,0.08) 100%)`,
                  minHeight: 420,
                  aspectRatio: '16 / 10',
                }}
                onClick={() => setSelectedFrameId('')}
              >
                {frames.length === 0 ? (
                  <div className="flex h-full min-h-[420px] items-center justify-center px-6 text-center text-gray-500">
                    Choose approved artworks on the left to start building your exhibition wall.
                  </div>
                ) : (
                  frames.map((frame) => {
                    const artwork = selectedArtworks.find((item) => item.id === frame.id);
                    if (!artwork) return null;
                    const isActive = selectedFrameId === frame.id;
                    return (
                      <button
                        key={frame.id}
                        type="button"
                        onPointerDown={(event) => handlePointerDown(event, frame)}
                        onClick={(event) => {
                          event.stopPropagation();
                          setSelectedFrameId(frame.id);
                        }}
                        className={`absolute overflow-hidden rounded-[0.8rem] border bg-white shadow-lg transition ${isActive ? 'ring-4 ring-orange-300' : ''}`}
                        style={{
                          left: `${frame.x}%`,
                          top: `${frame.y}%`,
                          width: `${frame.w}%`,
                          height: `${frame.h}%`,
                          borderColor: frameStyleConfig.accent,
                          boxShadow: `0 18px 28px ${frameStyleConfig.shadow}`,
                        }}
                      >
                        <div className="h-full w-full p-3" style={{ background: frameStyleConfig.border }}>
                          <div className="h-full w-full overflow-hidden rounded-[0.45rem] bg-white">
                            <img src={artwork.artwork_image_url} alt={artwork.artwork_title || 'Artwork'} className="h-full w-full object-cover" draggable={false} />
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              {message ? <p className="mt-4 text-sm font-semibold text-emerald-700">{message}</p> : null}
              {error ? <p className="mt-4 text-sm font-semibold text-red-600">{error}</p> : null}
            </section>

            <aside className="space-y-4">
              <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4">
                <p className="text-xs font-black uppercase tracking-[0.22em] text-[#4A148C]">Layouts</p>
                <div className="mt-3 space-y-2">
                  {exhibitionWallPresets.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setPreset(item.id)}
                      className={`w-full rounded-2xl border px-4 py-3 text-left transition ${
                        preset === item.id ? 'border-[#FF6F00] bg-orange-50' : 'border-slate-200 bg-white'
                      }`}
                    >
                      <span className="block text-sm font-black text-gray-950">{item.label}</span>
                      <span className="mt-1 block text-xs font-medium text-gray-500">{item.description}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4">
                <p className="text-xs font-black uppercase tracking-[0.22em] text-[#4A148C]">Wall color</p>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {wallPalettes.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setWallColor(item.value)}
                      className={`rounded-2xl border p-3 text-left text-xs font-bold ${wallColor === item.value ? 'border-[#FF6F00]' : 'border-slate-200'}`}
                    >
                      <span className="mb-2 block h-8 rounded-xl border border-white/60" style={{ background: item.value }} />
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4">
                <p className="text-xs font-black uppercase tracking-[0.22em] text-[#4A148C]">Frame style</p>
                <div className="mt-3 space-y-2">
                  {defaultFrameStyles.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setFrameStyle(item.id)}
                      className={`flex w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left ${frameStyle === item.id ? 'border-[#FF6F00] bg-orange-50' : 'border-slate-200'}`}
                    >
                      <span className="h-8 w-8 rounded-lg border-4" style={{ borderColor: item.border, background: '#fff', boxShadow: `0 8px 12px ${item.shadow}` }} />
                      <span className="text-sm font-black text-gray-950">{item.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-[1.5rem] border border-slate-200 bg-white p-4">
                <p className="text-xs font-black uppercase tracking-[0.22em] text-[#4A148C]">Selected artwork</p>
                {!selectedFrame ? (
                  <p className="mt-3 text-sm text-gray-500">Click any placed artwork to nudge or resize it.</p>
                ) : (
                  <div className="mt-3 space-y-3">
                    <p className="text-sm font-black text-gray-950">
                      {selectedArtworks.find((item) => item.id === selectedFrame.id)?.artwork_title || 'Artwork'}
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <button type="button" className="btn-outline !px-4 !py-2 text-sm" onClick={() => updateSelectedFrame({ x: clamp(selectedFrame.x - 2, 0, 100 - selectedFrame.w) })}>Left</button>
                      <button type="button" className="btn-outline !px-4 !py-2 text-sm" onClick={() => updateSelectedFrame({ x: clamp(selectedFrame.x + 2, 0, 100 - selectedFrame.w) })}>Right</button>
                      <button type="button" className="btn-outline !px-4 !py-2 text-sm" onClick={() => updateSelectedFrame({ y: clamp(selectedFrame.y - 2, 0, 100 - selectedFrame.h) })}>Up</button>
                      <button type="button" className="btn-outline !px-4 !py-2 text-sm" onClick={() => updateSelectedFrame({ y: clamp(selectedFrame.y + 2, 0, 100 - selectedFrame.h) })}>Down</button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <button type="button" className="btn-outline !px-4 !py-2 text-sm" onClick={() => updateSelectedFrame({ w: clamp(selectedFrame.w - 3, 12, 80), h: clamp(selectedFrame.h - 3, 12, 86) })}>Smaller</button>
                      <button type="button" className="btn-outline !px-4 !py-2 text-sm" onClick={() => updateSelectedFrame({ w: clamp(selectedFrame.w + 3, 12, 80), h: clamp(selectedFrame.h + 3, 12, 86) })}>Larger</button>
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
}
